/* eslint-disable react-refresh/only-export-components -- renderer plus small block helpers */
import { SafeMarkdown } from '../content/SafeMarkdown'
import type { DirectiveBlock, LessonBlock } from '../content/directiveParser'
import { getInteraction } from '../interactions/registry'
import { registerDefaultInteractions } from '../interactions/register'
import { useLessonState } from './lessonState'
import styles from './LessonContent.module.css'

// Ensure the known interactions are registered before any lesson renders.
registerDefaultInteractions()

/**
 * Renders parsed lesson blocks: prose through the safe Markdown renderer, and
 * directives through the component registry. Each interaction is wired to the
 * lesson state boundary for saved responses, response changes, and completion —
 * components never touch persistence directly. Unknown directives degrade to an
 * understandable static fallback so a lesson never breaks.
 */
export function LessonContent({ blocks }: { blocks: LessonBlock[] }) {
  return (
    <div className={styles.content}>
      {blocks.map((block, index) =>
        block.kind === 'prose' ? (
          <SafeMarkdown key={index} markdown={block.markdown} />
        ) : (
          <div key={block.id || index} id={activityAnchorId(block.id)}>
            <Directive block={block} />
          </div>
        ),
      )}
    </div>
  )
}

/** DOM id for an interaction, used to resume at the last incomplete activity. */
export function activityAnchorId(interactionId: string): string {
  return `activity-${interactionId}`
}

/** The ordered interaction ids in a lesson (its activities). */
export function getInteractionIds(blocks: LessonBlock[]): string[] {
  return blocks.filter((block) => block.kind === 'directive').map((block) => block.id)
}

function Directive({ block }: { block: DirectiveBlock }) {
  const lessonState = useLessonState()
  const Component = getInteraction(block.name)
  if (!Component) {
    return <UnknownDirective block={block} />
  }
  return (
    <Component
      id={block.id}
      config={block.config}
      savedResponse={lessonState.getResponse(block.id)}
      onResponseChange={(response) => lessonState.setResponse(block.id, response)}
      onComplete={() => lessonState.markComplete(block.id)}
    />
  )
}

/** Static, safe fallback for a directive with no registered component. */
function UnknownDirective({ block }: { block: DirectiveBlock }) {
  return (
    <div className={styles.unknown} role="note">
      <p className={styles.unknownTitle}>
        Interactive element “{block.name}” isn’t available in this version.
      </p>
      <p className={styles.unknownNote}>
        The lesson references an interaction this app doesn’t recognize. Its content is shown below
        so nothing is lost.
      </p>
      <pre className={styles.unknownRaw}>{block.raw}</pre>
    </div>
  )
}
